import { z } from 'zod';
import { AvailabilityFormSchema } from './Availability.schema';
import { guestSchema } from './Guest.schema';

export const ExperienceStepSchema = z
  .object({
    guestOptions: z.array(
      z.object({
        timeslots: z.array(z.array(AvailabilityFormSchema.optional())),
      })
    ),
    guestDetails: z.array(guestSchema),
    guestsHaveDifferentTimes: z.boolean().default(true),
  })
  .refine(
    ({ guestOptions, guestDetails, guestsHaveDifferentTimes }) => {
      const guestDetailsValid = guestDetails.every((guest) => guestSchema.safeParse(guest).success);

      if (guestsHaveDifferentTimes) {
        const allTimeslotsValid = guestOptions.every((opt) => {
          return opt.timeslots.every((ts) => {
            if (!ts || ts.length === 0) return true;
            return ts.every((timeslot) => AvailabilityFormSchema.safeParse(timeslot).success);
          });
        });

        const hasAtLeastOneTimeslot = guestOptions.some((opt) =>
          opt.timeslots.some((ts) => ts && ts.length > 0)
        );

        return allTimeslotsValid && guestDetailsValid && hasAtLeastOneTimeslot;
      }
      const firstGuestTimeslots = guestOptions[0]?.timeslots || [];
      const firstGuestValid = firstGuestTimeslots.every((ts) => {
        if (!ts || ts.length === 0) return true;
        return ts.every((timeslot) => AvailabilityFormSchema.safeParse(timeslot).success);
      });

      const hasAtLeastOneTimeslot = firstGuestTimeslots.some((ts) => ts && ts.length > 0);

      return firstGuestValid && guestDetailsValid && hasAtLeastOneTimeslot;
    },
    {
      message: "Guests' timeslots do not meet the required criteria.",
    }
  );

export type ExperienceStep = z.infer<typeof ExperienceStepSchema>;

export const ExperienceTypeSchema = z.union([
  z.literal('spa-treatments'),
  z.literal('spa-facilities'),
  z.literal('spa-classes'),
  z.literal('spa-extras'),
]);
export type ExperienceType = z.infer<typeof ExperienceTypeSchema>;

export const PackageExperienceSchema = z.object({
  dayNumber: z.number(),
  dayNumberRange: z.array(z.number()).optional(),
  delayAfter: z.number().optional(),
  id: z.string(),
  numberOfSelectionsRequired: z.number().default(0),
  options: z.array(
    z.object({
      itemId: z.string(),
      itemType: ExperienceTypeSchema,
      name: z.string(),
      meta: z.object({
        entityId: z.string(),
        entityType: z.literal('SpaPackageConfigurationGroupOption'),
      }),
    })
  ),
  meta: z.object({
    entityId: z.string(),
    entityType: z.literal('SpaPackageConfigurationGroup'),
  }),
});

export type PackageExperience = z.infer<typeof PackageExperienceSchema>;

export type OptionGroup = {
  count: number;
  dayNumber: number;
  delayAfter?: number;
  id: number;
  isIncluded: boolean;
  optionGroupId?: number | string;
  optionId: number | string;
  type: ExperienceType;
};

export type PackageExperienceOptions = PackageExperience['options'];
